import { createFileRoute } from '@tanstack/react-router'
import { CricketHeader } from '#/components/layout/CricketHeader'
import { BottomNav } from '#/components/layout/BottomNav'
import { Plus, Edit2, Trash2, ArrowLeft, Camera, User, Menu } from 'lucide-react'
import { useState } from 'react'
import { useCricketStore } from '#/store/cricketStore'
import { ConfirmDialog, Dialog, DialogContent, DialogHeader, DialogTitle } from '#/components/ui/dialog'
import type { TeamRecord } from '#/types/cricket'

export const Route = createFileRoute('/teams')({
  component: TeamsScreen,
})

const AVATAR_COLORS = [
  'bg-[#7CB342] text-white', // Govind Team green-lime
  'bg-[#C0CA33] text-white', // Naveen Team yellow-lime
  'bg-[#26A69A] text-white',
  'bg-[#42A5F5] text-white',
  'bg-[#AB47BC] text-white',
]

function TeamsScreen() {
  const teams = useCricketStore((state) => state.teamsList)
  const savedMatches = useCricketStore((state) => state.savedMatches)
  const addTeam = useCricketStore((state) => state.addTeam)
  const updateTeam = useCricketStore((state) => state.updateTeam)
  const deleteTeam = useCricketStore((state) => state.deleteTeam)
  const addPlayerToTeam = useCricketStore((state) => state.addPlayerToTeam)
  const removePlayerFromTeam = useCricketStore((state) => state.removePlayerFromTeam)
  const updatePlayerInTeam = useCricketStore((state) => state.updatePlayerInTeam)

  // Navigation states: 'list' | 'team_detail' | 'add_player' | 'player_stats'
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null)
  const [selectedPlayerName, setSelectedPlayerName] = useState<string | null>(null)
  const [isAddingPlayer, setIsAddingPlayer] = useState(false)
  const [newPlayerNameInput, setNewPlayerNameInput] = useState('')

  // Edit player name modal
  const [editingPlayerName, setEditingPlayerName] = useState<string | null>(null)
  const [editPlayerInput, setEditPlayerInput] = useState('')
  const [playerToDelete, setPlayerToDelete] = useState<string | null>(null)

  // Team modals
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [teamName, setTeamName] = useState('')
  const [teamToDelete, setTeamToDelete] = useState<{ id: string; name: string } | null>(null)

  // Player Stats tab: 'batting' | 'bowling' | 'fielding'
  const [statsTab, setStatsTab] = useState<'batting' | 'bowling' | 'fielding'>('batting')

  const selectedTeam: TeamRecord | undefined = teams.find((t) => t.id === selectedTeamId)

  // Calculate dynamic stats for a player across all saved matches
  const calculatePlayerStats = (playerName: string) => {
    let matchesCount = 0
    let inningsCount = 0
    let runs = 0
    let notOuts = 0
    let bestScore = 0
    let fours = 0
    let sixes = 0
    let ballsFaced = 0
    let ducks = 0
    let thirties = 0
    let fifties = 0
    let hundreds = 0

    // Bowling stats
    let bowlingMatchesCount = 0
    let bowlingInningsCount = 0
    let legalBalls = 0
    let maidens = 0
    let runsConceded = 0
    let wickets = 0
    let bestWickets = 0
    let bestConceded = 999
    let wides = 0
    let noBalls = 0
    let dotBalls = 0
    let fourWickets = 0
    let fiveWickets = 0

    // Fielding stats
    let catches = 0
    let stumpings = 0
    let runOuts = 0

    savedMatches.forEach((m) => {
      const inningsList = [m.innings1, m.innings2].filter(Boolean)
      let playerPlayed = false
      let playerBatted = false
      let playerBowled = false

      inningsList.forEach((inn) => {
        if (!inn) return
        const b = inn.batsmen?.[playerName]
        if (b) {
          playerPlayed = true
          playerBatted = true
          runs += b.runs
          ballsFaced += b.balls
          fours += b.fours
          sixes += b.sixes
          if (b.runs > bestScore) bestScore = b.runs
          if (!b.isOut) notOuts++
          if (b.isOut && b.runs === 0) ducks++
          if (b.runs >= 100) hundreds++
          else if (b.runs >= 50) fifties++
          else if (b.runs >= 30) thirties++
        }

        const bw = inn.bowlers?.[playerName]
        if (bw) {
          playerPlayed = true
          playerBowled = true
          legalBalls += bw.legalBalls
          maidens += bw.maidens
          runsConceded += bw.runsConceded
          wickets += bw.wickets
          if (
            bw.wickets > bestWickets ||
            (bw.wickets === bestWickets && bw.runsConceded < bestConceded)
          ) {
            bestWickets = bw.wickets
            bestConceded = bw.runsConceded
          }
          if (bw.wickets >= 5) fiveWickets++
          else if (bw.wickets === 4) fourWickets++
        }

        // Check deliveries for extras & fielding
        inn.deliveryHistory?.forEach((d) => {
          if (d.bowler === playerName) {
            if (d.extraType === 'wide') wides += d.extraRuns
            if (d.extraType === 'no_ball') noBalls += d.extraRuns
            if (d.runsScored === 0 && d.extraRuns === 0) dotBalls++
          }
          if (d.dismissedPlayer && d.wicketType) {
            // Dismissals recorded
          }
        })
      })

      if (playerPlayed) matchesCount++
      if (playerBatted) inningsCount++
      if (playerBowled) {
        bowlingMatchesCount++
        bowlingInningsCount++
      }
    })

    const oversStr = `${Math.floor(legalBalls / 6)}.${legalBalls % 6}`
    const strikeRate = ballsFaced > 0 ? ((runs / ballsFaced) * 100).toFixed(2) : '0.00'
    const dismissals = inningsCount - notOuts
    const battingAvg = dismissals > 0 ? (runs / dismissals).toFixed(2) : (runs > 0 ? runs.toFixed(2) : '0.00')

    const bowlingOversNum = Math.floor(legalBalls / 6) + (legalBalls % 6) / 6
    const ecoRate = bowlingOversNum > 0 ? (runsConceded / bowlingOversNum).toFixed(2) : '0.00'
    const bowlingStrikeRate = wickets > 0 ? (legalBalls / wickets).toFixed(2) : '0.00'
    const bowlingAvg = wickets > 0 ? (runsConceded / wickets).toFixed(2) : '0.00'
    const bestBowlingStr = bestWickets > 0 ? `${bestWickets}/${bestConceded}` : '-'

    return {
      matchesCount,
      inningsCount,
      runs,
      notOuts,
      bestScore,
      strikeRate,
      battingAvg,
      fours,
      sixes,
      thirties,
      fifties,
      hundreds,
      ducks,
      // Bowling
      bowlingMatchesCount,
      bowlingInningsCount,
      oversStr,
      maidens,
      wickets,
      runsConceded,
      bestBowlingStr,
      ecoRate,
      bowlingStrikeRate,
      bowlingAvg,
      wides,
      noBalls,
      dotBalls,
      fourWickets,
      fiveWickets,
      // Fielding
      catches,
      stumpings,
      runOuts,
    }
  }

  const handleOpenAdd = () => {
    setEditingId(null)
    setTeamName('')
    setShowModal(true)
  }

  const handleOpenEdit = (id: string, name: string) => {
    setEditingId(id)
    setTeamName(name)
    setShowModal(true)
  }

  const handleSaveTeam = (e: React.FormEvent) => {
    e.preventDefault()
    if (!teamName.trim()) return

    if (editingId) {
      updateTeam(editingId, teamName.trim())
    } else {
      addTeam(teamName.trim())
    }
    setShowModal(false)
    setTeamName('')
    setEditingId(null)
  }

  const handleDeleteTeam = (id: string, name: string) => {
    setTeamToDelete({ id, name })
  }

  const handleSaveNewPlayer = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newPlayerNameInput.trim() || !selectedTeam) return
    addPlayerToTeam(selectedTeam.id, newPlayerNameInput.trim())
    setNewPlayerNameInput('')
    setIsAddingPlayer(false)
  }

  const handleSaveEditPlayer = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editPlayerInput.trim() || !selectedTeam || !editingPlayerName) return
    updatePlayerInTeam(selectedTeam.id, editingPlayerName, editPlayerInput.trim())
    setEditingPlayerName(null)
    setEditPlayerInput('')
  }

  // SCREEN 30: Add Player Screen
  if (isAddingPlayer && selectedTeam) {
    return (
      <div className="flex-1 flex flex-col min-h-screen bg-[var(--theme-bg,#f5f6f8)]">
        {/* Header Bar matching Screen 30 */}
        <header className="bg-[var(--color-cricket-primary)] text-white px-4 py-3.5 flex items-center justify-between shadow-sm sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsAddingPlayer(false)}
              className="p-1 hover:bg-white/10 rounded-full transition-colors active:scale-90"
              aria-label="Back"
            >
              <ArrowLeft className="size-6" />
            </button>
            <h1 className="text-xl font-bold tracking-tight">Add Player</h1>
          </div>
          <button
            onClick={handleSaveNewPlayer}
            className="text-white text-sm font-bold tracking-wide uppercase hover:opacity-90 active:scale-95 transition-all"
          >
            SAVE
          </button>
        </header>

        <div className="p-6 flex flex-col items-center flex-1">
          {/* Circular Camera Avatar */}
          <div className="size-24 rounded-full bg-[var(--color-cricket-primary)] flex items-center justify-center text-white ring-4 ring-gray-200 dark:ring-neutral-800 shadow-md my-8">
            <Camera className="size-10" />
          </div>

          {/* Underlined Player Name Input */}
          <div className="w-full max-w-sm mt-4">
            <input
              type="text"
              placeholder="Enter player name"
              value={newPlayerNameInput}
              onChange={(e) => setNewPlayerNameInput(e.target.value)}
              className="w-full bg-transparent border-b-2 border-gray-300 dark:border-neutral-700 focus:border-[var(--color-cricket-primary)] text-gray-900 dark:text-white py-2 px-1 text-base outline-none transition-colors"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleSaveNewPlayer(e)
                }
              }}
            />
          </div>
        </div>
      </div>
    )
  }

  // SCREEN 27, 28, 29: Player Career Stats Screen
  if (selectedPlayerName && selectedTeam) {
    const stats = calculatePlayerStats(selectedPlayerName)

    return (
      <div className="flex-1 flex flex-col min-h-screen bg-[var(--theme-bg,#f5f6f8)] pb-10">
        {/* Top Header with Back button, Avatar and Name */}
        <div className="bg-[var(--color-cricket-primary)] text-white px-4 pt-3 pb-8 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => setSelectedPlayerName(null)}
              className="p-1 hover:bg-white/10 rounded-full transition-colors active:scale-90"
              aria-label="Back"
            >
              <ArrowLeft className="size-6" />
            </button>
          </div>

          <div className="flex flex-col items-center justify-center pt-2">
            <div className="size-24 rounded-full bg-white/20 backdrop-blur-xs flex items-center justify-center text-white/90 ring-4 ring-white/30 shadow-inner mb-3">
              <User className="size-14" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-white">{selectedPlayerName}</h1>
            <p className="text-xs text-white/80 mt-0.5">{selectedTeam.name}</p>
          </div>
        </div>

        {/* 3 Tabs: Batting, Bowling, Fielding */}
        <div className="bg-[var(--color-cricket-primary)] border-t border-white/20 flex text-white font-medium text-sm">
          <button
            onClick={() => setStatsTab('batting')}
            className={`flex-1 py-3 text-center transition-colors relative ${
              statsTab === 'batting' ? 'font-bold' : 'opacity-80 hover:opacity-100'
            }`}
          >
            Batting
            {statsTab === 'batting' && (
              <span className="absolute bottom-0 left-0 right-0 h-1 bg-white" />
            )}
          </button>
          <button
            onClick={() => setStatsTab('bowling')}
            className={`flex-1 py-3 text-center transition-colors relative ${
              statsTab === 'bowling' ? 'font-bold' : 'opacity-80 hover:opacity-100'
            }`}
          >
            Bowling
            {statsTab === 'bowling' && (
              <span className="absolute bottom-0 left-0 right-0 h-1 bg-white" />
            )}
          </button>
          <button
            onClick={() => setStatsTab('fielding')}
            className={`flex-1 py-3 text-center transition-colors relative ${
              statsTab === 'fielding' ? 'font-bold' : 'opacity-80 hover:opacity-100'
            }`}
          >
            Fielding
            {statsTab === 'fielding' && (
              <span className="absolute bottom-0 left-0 right-0 h-1 bg-white" />
            )}
          </button>
        </div>

        {/* Tab Content Cards */}
        <div className="p-4 flex-1">
          {statsTab === 'batting' && (
            <div className="grid grid-cols-3 gap-2.5">
              {[
                { label: 'Matches', value: stats.matchesCount },
                { label: 'Innings', value: stats.inningsCount },
                { label: 'Runs', value: stats.runs },
                { label: 'Not Outs', value: stats.notOuts },
                { label: 'Best Score', value: stats.bestScore },
                { label: 'Strike Rate', value: stats.strikeRate },
                { label: 'Average', value: stats.battingAvg },
                { label: 'Fours', value: stats.fours },
                { label: 'Sixes', value: stats.sixes },
                { label: 'Thirties', value: stats.thirties },
                { label: 'Fifties', value: stats.fifties },
                { label: 'Hundreds', value: stats.hundreds },
                { label: 'Ducks', value: stats.ducks },
              ].map((card, idx) => (
                <div
                  key={idx}
                  className="theme-card rounded-lg p-3 text-center shadow-xs flex flex-col justify-center min-h-[72px]"
                >
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                    {card.label}
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white mt-1">
                    {card.value}
                  </span>
                </div>
              ))}
            </div>
          )}

          {statsTab === 'bowling' && (
            <div className="grid grid-cols-3 gap-2.5">
              {[
                { label: 'Matches', value: stats.bowlingMatchesCount },
                { label: 'Innings', value: stats.bowlingInningsCount },
                { label: 'Overs', value: stats.oversStr },
                { label: 'Maidens', value: stats.maidens },
                { label: 'Wickets', value: stats.wickets },
                { label: 'Runs', value: stats.runsConceded },
                { label: 'B. Bowling', value: stats.bestBowlingStr },
                { label: 'Eco. Rate', value: stats.ecoRate },
                { label: 'Strike Rate', value: stats.bowlingStrikeRate },
                { label: 'Average', value: stats.bowlingAvg },
                { label: 'Wides', value: stats.wides },
                { label: 'No Balls', value: stats.noBalls },
                { label: 'Dots balls', value: stats.dotBalls },
                { label: '4 Wickets', value: stats.fourWickets },
                { label: '5 Wickets', value: stats.fiveWickets },
              ].map((card, idx) => (
                <div
                  key={idx}
                  className="theme-card rounded-lg p-3 text-center shadow-xs flex flex-col justify-center min-h-[72px]"
                >
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                    {card.label}
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white mt-1">
                    {card.value}
                  </span>
                </div>
              ))}
            </div>
          )}

          {statsTab === 'fielding' && (
            <div className="grid grid-cols-3 gap-2.5">
              {[
                { label: 'Matches', value: stats.matchesCount },
                { label: 'Catches', value: stats.catches },
                { label: 'Stumpings', value: stats.stumpings },
                { label: 'Run Outs', value: stats.runOuts },
              ].map((card, idx) => (
                <div
                  key={idx}
                  className="theme-card rounded-lg p-3 text-center shadow-xs flex flex-col justify-center min-h-[72px]"
                >
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                    {card.label}
                  </span>
                  <span className="text-base font-bold text-gray-900 dark:text-white mt-1">
                    {card.value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  // SCREEN 26: Team Players Roster Screen
  if (selectedTeam) {
    const players = selectedTeam.players || []

    return (
      <div className="flex-1 flex flex-col min-h-screen bg-[var(--theme-bg,#f5f6f8)] pb-20">
        {/* Header Bar */}
        <header className="bg-[var(--color-cricket-primary)] text-white px-4 py-3.5 flex items-center justify-between shadow-sm sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedTeamId(null)}
              className="p-1 hover:bg-white/10 rounded-full transition-colors active:scale-90"
              aria-label="Back"
            >
              <ArrowLeft className="size-6" />
            </button>
            <h1 className="text-xl font-bold tracking-tight truncate max-w-[220px]">
              {selectedTeam.name}
            </h1>
          </div>
        </header>

        {/* Players List */}
        <div className="p-4 space-y-3 flex-1">
          {players.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-28 text-center">
              <p className="text-gray-600 dark:text-gray-300 text-base max-w-xs leading-relaxed font-medium">
                No players added to this team yet.
                <br />
                Tap the + button below to add players.
              </p>
            </div>
          ) : (
            players.map((playerName) => (
              <div
                key={playerName}
                onClick={() => setSelectedPlayerName(playerName)}
                className="theme-card rounded-xl p-3.5 shadow-sm border flex items-center justify-between cursor-pointer active:opacity-95 transition-all"
              >
                <div className="flex items-center gap-3.5">
                  <div className="size-11 rounded-full bg-[var(--color-cricket-primary)] flex items-center justify-center text-white">
                    <User className="size-6" />
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-white text-base">
                    {playerName}
                  </span>
                </div>

                {/* Edit, Delete, and Reorder handle */}
                <div
                  className="flex items-center gap-3 text-gray-400"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => {
                      setEditingPlayerName(playerName)
                      setEditPlayerInput(playerName)
                    }}
                    className="p-1 hover:text-[var(--color-cricket-primary)] transition-colors active:scale-90"
                    aria-label="Edit player"
                  >
                    <Edit2 className="size-4" />
                  </button>
                  <button
                    onClick={() => setPlayerToDelete(playerName)}
                    className="p-1 hover:text-red-500 transition-colors active:scale-90"
                    aria-label="Delete player"
                  >
                    <Trash2 className="size-4" />
                  </button>
                  <div className="text-gray-300 dark:text-neutral-600 cursor-grab">
                    <Menu className="size-5" />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Floating Action Button (FAB) for adding player */}
        <button
          onClick={() => {
            setNewPlayerNameInput('')
            setIsAddingPlayer(true)
          }}
          className="fixed bottom-6 right-6 size-14 rounded-full bg-[var(--color-cricket-primary)] text-white flex items-center justify-center shadow-lg hover:opacity-95 active:scale-95 transition-all z-30"
          aria-label="Add Player"
        >
          <Plus className="size-7 stroke-[2.5]" />
        </button>

        {/* Edit Player Name Dialog */}
        <Dialog open={Boolean(editingPlayerName)} onOpenChange={(open) => !open && setEditingPlayerName(null)}>
          <DialogContent className="max-w-sm p-6">
            <DialogHeader>
              <DialogTitle>Edit Player Name</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveEditPlayer} className="space-y-4 pt-2">
              <input
                type="text"
                placeholder="Player Name"
                value={editPlayerInput}
                onChange={(e) => setEditPlayerInput(e.target.value)}
                className="w-full border-b border-gray-300 dark:border-neutral-700 py-2 px-1 text-gray-800 dark:text-white bg-transparent focus:outline-none focus:border-[var(--color-cricket-primary)] text-base"
                autoFocus
                required
              />
              <div className="flex justify-end gap-2.5 pt-3">
                <button
                  type="button"
                  onClick={() => setEditingPlayerName(null)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm bg-[var(--color-cricket-primary)] text-white rounded-lg font-semibold hover:opacity-95 transition-all active:scale-95 shadow-xs"
                >
                  Save
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Player Dialog */}
        <ConfirmDialog
          open={Boolean(playerToDelete)}
          onOpenChange={(open) => !open && setPlayerToDelete(null)}
          title="Delete Player"
          description={`Are you sure you want to remove ${playerToDelete} from ${selectedTeam.name}?`}
          confirmText="Remove"
          cancelText="Cancel"
          variant="danger"
          onConfirm={() => {
            if (playerToDelete) {
              removePlayerFromTeam(selectedTeam.id, playerToDelete)
              setPlayerToDelete(null)
            }
          }}
        />
      </div>
    )
  }

  // SCREEN 25: Main Teams List
  return (
    <div className="flex-1 flex flex-col justify-between min-h-screen pb-20 bg-[var(--theme-bg,#f5f6f8)]">
      <CricketHeader title="Cricket scorer" />

      {/* Main Teams List */}
      <div className="p-4 space-y-3 flex-1">
        {teams.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-28 text-center">
            <p className="text-gray-800 dark:text-gray-200 text-base max-w-xs leading-relaxed font-medium">
              You don't have any team yet.
              <br />
              Please create teams.
            </p>
          </div>
        ) : (
          teams.map((t, index) => {
            const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length]
            const playerCount = t.players?.length || 0

            return (
              <div
                key={t.id}
                onClick={() => setSelectedTeamId(t.id)}
                className="theme-card rounded-xl p-3.5 shadow-sm border flex items-center justify-between cursor-pointer active:opacity-95 transition-all"
              >
                <div className="flex items-center gap-3">
                  {/* Circular Avatar with team initials */}
                  <div
                    className={`size-12 rounded-full flex items-center justify-center font-bold text-base shadow-xs ${avatarColor}`}
                  >
                    {t.shortCode}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white text-base">
                      {t.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 space-x-2">
                      <span>Matches: <strong className="text-gray-800 dark:text-gray-200 font-medium">{t.matchesCount}</strong></span>
                      <span>Won: <strong className="text-gray-800 dark:text-gray-200 font-medium">{t.wonCount}</strong></span>
                      <span>Lost: <strong className="text-gray-800 dark:text-gray-200 font-medium">{t.lostCount}</strong></span>
                      {playerCount > 0 && (
                        <span>• <strong className="text-gray-800 dark:text-gray-200 font-medium">{playerCount} players</strong></span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Edit and Delete action icons */}
                <div
                  className="flex items-center gap-3 pr-1 text-gray-500 dark:text-gray-400"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => handleOpenEdit(t.id, t.name)}
                    className="p-1 hover:text-[var(--color-cricket-primary)] transition-colors active:scale-90"
                    aria-label="Edit team"
                  >
                    <Edit2 className="size-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteTeam(t.id, t.name)}
                    className="p-1 hover:text-red-600 transition-colors active:scale-90"
                    aria-label="Delete team"
                  >
                    <Trash2 className="size-5" />
                  </button>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Floating Action Button (FAB) at bottom-right */}
      <button
        onClick={handleOpenAdd}
        className="fixed bottom-20 right-6 size-14 rounded-full bg-[var(--color-cricket-primary)] text-white flex items-center justify-center shadow-lg hover:opacity-95 active:scale-95 transition-all z-30"
        aria-label="Add Team"
      >
        <Plus className="size-7 stroke-[2.5]" />
      </button>

      {/* Add/Edit Team Custom Dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-sm p-6">
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Edit Team' : 'Create New Team'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTeam} className="space-y-4 pt-2">
            <input
              type="text"
              placeholder="Team Name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              className="w-full border-b border-gray-300 dark:border-neutral-700 py-2 px-1 text-gray-800 dark:text-white bg-transparent focus:outline-none focus:border-[var(--color-cricket-primary)] text-base"
              autoFocus
              required
            />
            <div className="flex justify-end gap-2.5 pt-3">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 text-sm bg-[var(--color-cricket-primary)] text-white rounded-lg font-semibold hover:opacity-95 transition-all active:scale-95 shadow-xs"
              >
                {editingId ? 'Save' : 'Create'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Custom Confirmation Dialog for deleting team */}
      <ConfirmDialog
        open={Boolean(teamToDelete)}
        onOpenChange={(open) => !open && setTeamToDelete(null)}
        title="Delete Team"
        description={`Are you sure you want to delete ${teamToDelete?.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={() => {
          if (teamToDelete) {
            deleteTeam(teamToDelete.id)
            setTeamToDelete(null)
          }
        }}
      />

      <BottomNav />
    </div>
  )
}
